import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  ArrowRight,
  Baby,
  Calendar,
  Check,
  ChevronDown,
  ChevronUp,
  CircleDot,
  Clock,
  CreditCard,
  FileText,
  MessageCircle,
  Shield,
  Smile,
  Sparkles,
  Stethoscope,
  Phone,
} from 'lucide-react';
import { CLINIC_INFO, FIRST_VISIT_STEPS, SERVICES } from '../data/mock';

const iconMap = {
  Stethoscope,
  Smile,
  Sparkles,
  AlertCircle,
  Baby,
  CircleDot,
};

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-[var(--border-light)]">
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className="w-full py-4 flex items-center justify-between text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-2 rounded-md"
        aria-expanded={isOpen}
      >
        <span className="text-sm font-medium text-[var(--text-primary)] pr-4">{question}</span>
        {isOpen ? (
          <ChevronUp size={18} className="text-[var(--text-muted)] flex-shrink-0" />
        ) : (
          <ChevronDown size={18} className="text-[var(--text-muted)] flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="pb-4">
          <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
};

const ServicesPage = () => {
  const [activeServiceId, setActiveServiceId] = useState(() => window.location.hash.replace('#', '') || 'evaluacion');

  useEffect(() => {
    const handleHashChange = () => setActiveServiceId(window.location.hash.replace('#', '') || 'evaluacion');
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const whatsappBase = useMemo(() => `https://wa.me/${CLINIC_INFO.whatsapp}?text=`, []);

  return (
    <div>
      <section className="bg-white pt-10 pb-8">
        <div className="container">
          <div className="max-w-3xl">
            <p className="text-caption text-[var(--color-accent)] mb-3">Nuestros servicios</p>
            <h1 className="text-h1 mb-4">Tratamientos para cada necesidad</h1>
            <p className="text-body-lg mb-6">
              Desde prevención hasta estética avanzada. Cada procedimiento es realizado por especialistas certificados
              con tecnología de punta.
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href={CLINIC_INFO.calendlyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary"
              >
                <Calendar size={18} />
                Agendar cita
              </a>
              <a href="#selector" className="btn btn-secondary">
                Ver opciones <ArrowRight size={16} />
              </a>
            </div>

            <p className="text-body-sm mt-4 text-[var(--text-muted)]">
              Radiografías digitales • Plan por escrito • Opciones de pago
            </p>
          </div>
        </div>
      </section>

      <section id="selector" className="bg-[var(--bg-secondary)] py-6">
        <div className="container">
          <div
            className="flex gap-3 overflow-x-auto no-scrollbar md:grid md:grid-cols-3 lg:grid-cols-6 md:gap-4"
            style={{ scrollSnapType: 'x mandatory' }}
          >
            {SERVICES.map((service) => {
              const IconComponent = iconMap[service.icon] || Stethoscope;
              const active = activeServiceId === service.id;
              return (
                <a
                  key={service.id}
                  href={`#${service.id}`}
                  onClick={() => setActiveServiceId(service.id)}
                  className={`card p-4 text-center transition-colors group flex-shrink-0 w-[160px] md:w-auto ${
                    active ? 'border-[var(--color-accent)] bg-white' : 'hover:border-[var(--color-accent)]'
                  }`}
                  style={{ scrollSnapAlign: 'start' }}
                >
                  <div
                    className={`w-12 h-12 mx-auto mb-3 rounded-xl flex items-center justify-center transition-colors ${
                      active ? 'bg-[var(--color-accent)]' : 'bg-[var(--color-accent-bg)] group-hover:bg-[var(--color-accent)]'
                    }`}
                  >
                    <IconComponent
                      size={22}
                      className={`transition-colors ${active ? 'text-white' : 'text-[var(--color-accent)] group-hover:text-white'}`}
                    />
                  </div>
                  <span className="text-sm font-medium text-[var(--text-primary)]">{service.shortTitle}</span>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-white py-12">
        <div className="container">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="text-h3 mb-3">Cómo funciona tu primera visita</h2>
            <p className="text-body">Un proceso simple, claro y sin presión.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {FIRST_VISIT_STEPS.map((step) => (
              <div key={step.step} className="text-center">
                <div className="w-12 h-12 mx-auto mb-3 bg-[var(--color-primary)] text-white rounded-full flex items-center justify-center text-lg font-bold">
                  {step.step}
                </div>
                <h3 className="font-semibold text-[var(--text-primary)] mb-1">{step.title}</h3>
                <p className="text-sm text-[var(--text-muted)] leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[var(--bg-secondary)]">
        <div className="container">
          {SERVICES.map((service, index) => {
            const IconComponent = iconMap[service.icon] || Stethoscope;
            const isEven = index % 2 === 0;
            const whatsappMessage = encodeURIComponent(
              `Hola, me interesa el servicio de ${service.title}. Mi nombre es ___, mi disponibilidad es ___.`
            );
            const isUrgency = service.id === 'urgencias';

            return (
              <div
                key={service.id}
                id={service.id}
                className="py-12 border-b border-[var(--border-light)] last:border-b-0"
              >
                <div
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center ${
                    isEven ? '' : 'lg:[direction:rtl]'
                  }`}
                >
                  <div className={isEven ? '' : 'lg:[direction:ltr]'}>
                    <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-lg">
                      <img src={service.image} alt={service.title} className="w-full h-full object-cover" />
                    </div>
                  </div>

                  <div className={isEven ? '' : 'lg:[direction:ltr]'}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-[var(--color-accent-bg)] rounded-xl flex items-center justify-center">
                        <IconComponent size={24} className="text-[var(--color-accent)]" />
                      </div>
                      <h2 className="text-h3">{service.title}</h2>
                    </div>

                    <p className="text-body-lg mb-6">{service.fullDescription}</p>

                    <div className="grid grid-cols-1 min-[360px]:grid-cols-3 gap-3 mb-6">
                      <div className="bg-white rounded-lg p-2.5 sm:p-3 text-center">
                        <Clock size={18} className="mx-auto mb-1 text-[var(--color-accent)]" />
                        <p className="text-xs text-[var(--text-muted)]">Duración</p>
                        <p className="text-sm font-medium">{service.duration}</p>
                      </div>
                      <div className="bg-white rounded-lg p-2.5 sm:p-3 text-center">
                        <Calendar size={18} className="mx-auto mb-1 text-[var(--color-accent)]" />
                        <p className="text-xs text-[var(--text-muted)]">Visitas</p>
                        <p className="text-sm font-medium">{service.sessions}</p>
                      </div>
                      <div className="bg-white rounded-lg p-2.5 sm:p-3 text-center">
                        <Shield size={18} className="mx-auto mb-1 text-[var(--color-accent)]" />
                        <p className="text-xs text-[var(--text-muted)]">Molestia</p>
                        <p className="text-sm font-medium">{service.painLevel}</p>
                      </div>
                    </div>

                    <div className="mb-6">
                      <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-3">Beneficios</h3>
                      <ul className="space-y-2">
                        {service.benefits.map((benefit, benefitIndex) => (
                          <li key={`${service.id}-${benefitIndex}`} className="flex items-start gap-2">
                            <Check size={16} className="text-[var(--color-accent)] flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-[var(--text-secondary)]">{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="bg-white rounded-lg p-4 mb-6">
                      <p className="text-xs text-[var(--text-muted)] uppercase tracking-wider mb-1">Ideal para</p>
                      <p className="text-sm text-[var(--text-primary)]">{service.recommendedFor}</p>
                    </div>

                    {service.faqs?.length > 0 && (
                      <div className="mb-6">
                        <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-2">
                          Preguntas frecuentes
                        </h3>
                        <div className="bg-white rounded-lg px-4">
                          {service.faqs.map((faq) => (
                            <FAQItem key={faq.q} question={faq.q} answer={faq.a} />
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex flex-col sm:flex-row gap-3">
                      {isUrgency ? (
                        <>
                          <a href={`tel:${CLINIC_INFO.phone}`} className="btn btn-primary justify-center">
                            <Phone size={18} />
                            Llamar ahora
                          </a>
                          <a
                            href={CLINIC_INFO.calendlyUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-secondary justify-center"
                          >
                            <Calendar size={18} />
                            Agendar cita
                          </a>
                        </>
                      ) : (
                        <>
                          <a
                            href={CLINIC_INFO.calendlyUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-primary justify-center"
                          >
                            <Calendar size={18} />
                            Agendar cita
                          </a>
                          <a href={`tel:${CLINIC_INFO.phone}`} className="btn btn-secondary justify-center">
                            <Phone size={18} />
                            Llamar
                          </a>
                        </>
                      )}
                      <a
                        href={`${whatsappBase}${whatsappMessage}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-whatsapp justify-center sm:ml-auto"
                      >
                        <MessageCircle size={18} />
                        WhatsApp
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section id="planes" className="bg-white py-12">
        <div className="container">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="text-h2 mb-3">Opciones de pago</h2>
            <p className="text-body-lg">
              Queremos que el costo no sea un obstáculo. Te ofrecemos diferentes formas de pago para tu comodidad.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            <div className="card p-6 text-center h-full">
              <div className="w-14 h-14 mx-auto mb-4 bg-[var(--color-accent-bg)] rounded-xl flex items-center justify-center">
                <CreditCard size={24} className="text-[var(--color-accent)]" />
              </div>
              <h3 className="text-h4 mb-2">Tarjetas</h3>
              <p className="text-body-sm mb-3">Aceptamos tarjetas. Meses sin intereses con bancos participantes.</p>
              <p className="text-xs text-[var(--text-muted)]">Visa • MasterCard • AMEX</p>
            </div>

            <div className="card p-6 text-center border-[var(--color-accent)] h-full">
              <div className="w-14 h-14 mx-auto mb-4 bg-[var(--color-accent)] rounded-xl flex items-center justify-center">
                <FileText size={24} className="text-white" />
              </div>
              <h3 className="text-h4 mb-2">Plan por etapas</h3>
              <p className="text-body-sm">Pagas por fase: diagnóstico → tratamiento → seguimiento.</p>
            </div>

            <div className="card p-6 text-center h-full">
              <div className="w-14 h-14 mx-auto mb-4 bg-[var(--color-accent-bg)] rounded-xl flex items-center justify-center">
                <Shield size={24} className="text-[var(--color-accent)]" />
              </div>
              <h3 className="text-h4 mb-2">Cotización clara</h3>
              <p className="text-body-sm">Antes de iniciar, recibes un presupuesto detallado por escrito.</p>
            </div>
          </div>

          <div className="bg-[var(--bg-secondary)] rounded-xl p-8 text-center">
            <p className="text-body mb-6">
              <strong>No necesitas decidir hoy.</strong> Agenda tu cita y te explicamos las opciones disponibles para tu
              caso, sin compromiso.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href={CLINIC_INFO.calendlyUrl} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
                <Calendar size={18} />
                Agendar cita
              </a>
              <a
                href={`https://wa.me/${CLINIC_INFO.whatsapp}?text=${encodeURIComponent(
                  'Hola, me gustaría conocer las opciones de pago para mi tratamiento.'
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-whatsapp"
              >
                <MessageCircle size={18} />
                WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-red-50 border-y border-red-100 py-6">
        <div className="container">
          <div className="flex flex-col md:flex-row items-center justify-between gap-5">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-red-500 rounded-xl flex items-center justify-center flex-shrink-0">
                <AlertCircle size={24} className="text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-red-900">Urgencia dental hoy</h3>
                <p className="text-sm text-red-700">Atención el mismo día (sujeto a disponibilidad).</p>
              </div>
            </div>
            <a href={`tel:${CLINIC_INFO.phone}`} className="btn bg-red-600 text-white hover:bg-red-700 flex-shrink-0">
              <Phone size={18} />
              Llamar ahora
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
